import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import dayjs from 'dayjs';

/**
 * Reusable chat body used in modal dialogs
 .* Props:
 *   messages      — array of { id, sender, senderName, text, sentAt }
 *   onSend(text)  — called when coordinator sends a message
 *   outgoingSender — the sender value that maps to the right-aligned (teal) bubble
 .*                    Defaults to 'contact' (portal ticket context)
 .*                    Use 'coordinator' for supplier correspondence
 .*/
export default function ModalChatBody({ messages, onSend, outgoingSender = 'contact' }) {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text) return;
    onSend(text);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const fmtTime = (iso) => dayjs(iso).format('MMM D, h:mm A');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 360 }}>
      <Box
        ref={scrollRef}
        sx={{ flex: 1,
          overflowY: 'auto',
          px: 2.5,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '4px', backgroundColor: 'var(--grey-300)' },
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center' }}>
              No messages yet. Send a message to get started.
            </Typography>
          </Box>
        )}
        {messages.map((msg) => {
          const isMe = msg.sender === outgoingSender;
          return (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              {!isMe && (
                <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', fontWeight: 500, mb: '3px', px: '2px' }}>
                  {msg.senderName}
                </Typography>
              )}
              <Box sx={{ maxWidth: '80%', px: '12px', py: '8px',
                borderRadius: isMe ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
                backgroundColor: isMe ? '#009688' : 'var(--grey-100)',
                color: isMe ? '#fff' : 'var(--grey-900)',
              }}>
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {msg.text}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px', px: '2px' }}>
                {fmtTime(msg.sentAt)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ borderTop: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0, ml: '10px', color: 'var(--grey-400)', '&:hover': { color: '#009688', backgroundColor: 'transparent' } }}
        >
          <AttachFileIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <TextField
          size="small" placeholder="Type a message…" multiline maxRows={3} fullWidth
          value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', borderRadius: 0, '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } },
            '& .MuiInputBase-input': { py: '10px', px: '4px' },
          }}
        />
        <IconButton
          size="small" disabled={!inputText.trim()} onClick={handleSend}
          sx={{ flexShrink: 0, mr: '10px', backgroundColor: '#009688', color: '#fff', borderRadius: '8px', width: 32, height: 32, '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' } }}
        >
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
